import {
  AlertDialog,
  AlertDialogBody,
  AlertDialogContent,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogOverlay,
  Button,
  Flex,
} from '@chakra-ui/react'
import { Heading, Text } from '@components/Common'
import { useLogout } from '@hooks/useLogout'
import { Helpers } from '@utils/helper'
import { useCallback, useRef, useState } from 'react'
import { useIdleTimer } from 'react-idle-timer'
import { getWaterAccounts } from '@api/waterAccount'
import { IconWarning } from '@assets/icons'
import { usePubSelector } from '@redux/hook'

export const TOTAL_TIMEOUT =
  1000 * 60 * Helpers.toNumber(process.env.REACT_APP_TIMEOUT)
export default function ExpiringPopup() {
  const accountSelected = usePubSelector(
    (state) => state.waterAccount.accountSelected,
  )
  const [isOpen, setOpen] = useState(false)
  const cancelRef = useRef<any>()

  const { logout } = useLogout()

  const onIdle = useCallback(() => {
    Helpers.setLastUpdated()
    Helpers.setLastIdle()
    setOpen(true)
  }, [])

  const onAction = useCallback((e) => Helpers.setLastUpdated(isOpen), [isOpen])

  const { reset } = useIdleTimer({
    timeout: TOTAL_TIMEOUT,
    element: document,
    onAction,
    onIdle,
    debounce: 500,
    crossTab: true,
    events: [
      'keydown',
      'wheel',
      'DOMMouseScroll',
      'mousewheel',
      'mousedown',
      'touchstart',
      'touchmove',
      'MSPointerDown',
      'MSPointerMove',
    ],
  })

  const onConfirm = useCallback(() => {
    const shouldBeClearCached = Helpers.checkShouldClearCached()
    if (!shouldBeClearCached) {
      Helpers.setLastUpdated()
      Helpers.resetLastIdle()
      reset()
    } else {
      logout()
    }
    if (accountSelected?.contractNumber) {
      try {
        getWaterAccounts([accountSelected.contractNumber])
      } catch (error) {
        console.error('recall on idle popup', error)
      }
    }
    setOpen(false)
  }, [reset, logout])

  return (
    <AlertDialog
      isOpen={isOpen}
      isCentered
      onClose={console.log}
      leastDestructiveRef={cancelRef}
    >
      <AlertDialogOverlay>
        <AlertDialogContent
          data-testid="expiring-dialog"
          zIndex={999}
          p={{ base: '16px 24px', lg: '40px' }}
          w={{ base: '100%', lg: '608px' }}
          maxW="auto"
        >
          <AlertDialogHeader p="0" mb="10px">
            <Flex
              sx={{
                'svg path': {
                  fill: 'primary.hyperLink',
                },
              }}
            >
              <IconWarning width="25px" height="25px" />
              <Heading
                as="h6"
                p="0 16px"
                fontSize={{ base: '18px', lg: '20px' }}
                color="primary.hyperLink"
                fontFamily="Frutiger"
              >
                Session timeout!
              </Heading>
            </Flex>
          </AlertDialogHeader>
          <AlertDialogBody pb="10px" pt="0" pr="0">
            <Text
              as="body"
              fontSize={{ base: '14px', lg: '16px' }}
              fontFamily="Arial, sans-serif"
              color="grey.100"
              pl="17px"
              lineHeight={{ base: '20px', lg: '22px' }}
            >
              Your session will expire in 5 minutes. Would you like to continue?
            </Text>
          </AlertDialogBody>
          <AlertDialogFooter p="0">
            <Button
              w={{ base: 'full', lg: 'auto' }}
              minW="104px"
              m={{ base: '0', lg: '0 24px 0 0' }}
              onClick={onConfirm}
            >
              Continue
            </Button>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialogOverlay>
    </AlertDialog>
  )
}
